import {useTranslation} from 'react-i18next';

import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetAllCurrentResidentsQuery,
  useGetAllUsersForInputQuery,
  useGetRoomsQuery,
  UserGroup,
} from '@/services/graphqlHooks';
import {localeCompareSort} from '@/utils/localeCompareSort';

import useEvacuationStore from '../stores/evacuationStore';
import {
  buildRoomSections,
  formatPersonName,
  formatRoomLabel,
  getEvacuationCounts,
  matchesSearch,
  sortByEvacuationStatus,
  sortByPersonName,
} from '../utils/evacuationHelpers';

export function useEvacuationPeople(search: string) {
  const {t} = useTranslation();
  const confirmedIds = useEvacuationStore(s => s.confirmedIds);
  const absentIds = useEvacuationStore(s => s.absentIds);
  const status = {confirmedIds, absentIds};

  const residentsQuery = useGetAllCurrentResidentsQuery(
    graphqlClient,
    undefined,
    {staleTime: 0}
  );
  const usersQuery = useGetAllUsersForInputQuery(graphqlClient);
  const roomsQuery = useGetRoomsQuery(graphqlClient);

  const residents = sortByPersonName(
    residentsQuery.data?.getAllCurrentResidents ?? []
  );
  const staff = sortByPersonName(
    (usersQuery.data?.getAllUsers ?? []).filter(
      user => user.group !== UserGroup.AuthorizedRelatives
    )
  );
  const rooms = [...(roomsQuery.data?.getRooms ?? [])].sort((a, b) =>
    localeCompareSort(a.roomNumber, b.roomNumber)
  );

  const roomLabelById: Record<string, string> = Object.fromEntries(
    rooms.map(room => [room.id, formatRoomLabel(room)])
  );
  const getRoomLabel = (roomId?: string | null) =>
    roomId ? roomLabelById[roomId] : undefined;

  const visibleResidents = sortByEvacuationStatus(
    residents.filter(resident =>
      matchesSearch(
        search,
        formatPersonName(resident.name),
        getRoomLabel(resident.roomId) ?? t('No room')
      )
    ),
    status
  );
  const visibleStaff = sortByEvacuationStatus(
    staff.filter(user => matchesSearch(search, formatPersonName(user.name))),
    status
  );

  return {
    isLoading:
      residentsQuery.isLoading || usersQuery.isLoading || roomsQuery.isLoading,
    isSearching: !!search.trim(),
    getRoomLabel,
    visibleResidents,
    visibleStaff,
    roomSections: buildRoomSections(
      visibleResidents,
      rooms,
      t('No room'),
      status
    ),
    counts: getEvacuationCounts(residents, staff, status),
  };
}
